"use client";

import { cn } from "@/utils/cn";

import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import * as React from "react";

import { memo } from "react";

/**
 * AlertDialog — shadcn primitive migrated to class-selector + co-located CSS.
 *
 * REFERENCE PATTERN: class-selector + co-located CSS (see shadcn-authoring.md
 * commit b35c0a615). Eighth execution of the shadcn-authoring template;
 * first Tier-3 (modal) primitive after Tier-1 (Button/Card/Badge) and
 * Tier-2 (Skeleton/Input/Textarea/Select).
 *
 * Public API preserved EXACTLY: every Radix import + integration verbatim;
 * all forwardRef + displayName preserved (where present); all 11 exports
 * unchanged. AlertDialogAction + AlertDialogCancel put Button's dw-button,
 * data-variant and data-size on the primitive's own button — wrapping them in
 * <Button> would nest a button inside an AlertDialogPrimitive.Action/Cancel
 * which itself renders a button (invalid HTML). A caller's data-variant
 * replaces the default one.
 *
 * Animation strategy mirrors Select (commit 4af955690): per-state combined
 * keyframes (fade + zoom + slide composed into a single transform).
 * AlertDialogContent's slide-from-upper-left-into-center semantic is
 * preserved by combining the base translate(-50%, -50%) centering with
 * compounded entrance offset (-100%, -98%). Reduced-motion override
 * disables enter/exit (consistent with Skeleton commit 4369425c4).
 *
 * Source: docs/implementation-blueprint.md §8.3 (class-selector pattern);
 *         .claude/agent-prompts/shadcn-authoring.md (binding template, commit b35c0a615);
 *         .claude/agent-prompts/atom-authoring-class-selector.md (shared rules);
 *         src/components/ui/shadcn/select.tsx (Tier-2 animation precedent, commit 4af955690);
 *         src/components/ui/shadcn/button.tsx (the data-variant / data-size axes).
 */

const AlertDialog = AlertDialogPrimitive.Root;

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = memo(
    React.forwardRef<React.ComponentRef<typeof AlertDialogPrimitive.Overlay>, React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>>(({ className, ...props }, ref) => <AlertDialogPrimitive.Overlay ref={ref} className={cn("dw-alert-dialog-overlay", className)} data-component="AlertDialogOverlay" data-location="src/components/ui/shadcn/alert-dialog.tsx" {...props} />)
);
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

const AlertDialogContent = memo(
    React.forwardRef<React.ComponentRef<typeof AlertDialogPrimitive.Content>, React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>>(({ className, ...props }, ref) => (
        <AlertDialogPortal data-component="AlertDialogContent" data-location="src/components/ui/shadcn/alert-dialog.tsx">
            <AlertDialogOverlay />
            <AlertDialogPrimitive.Content ref={ref} className={cn("dw-alert-dialog-content", className)} {...props} />
        </AlertDialogPortal>
    ))
);
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = memo(
    function AlertDialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
        return <div className={cn("dw-alert-dialog-header", className)} data-component="AlertDialogHeader" data-location="src/components/ui/shadcn/alert-dialog.tsx" {...props} />;
    }
);
AlertDialogHeader.displayName = "AlertDialogHeader";

const AlertDialogFooter = memo(
    function AlertDialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
        return <div className={cn("dw-alert-dialog-footer", className)} data-component="AlertDialogFooter" data-location="src/components/ui/shadcn/alert-dialog.tsx" {...props} />;
    }
);
AlertDialogFooter.displayName = "AlertDialogFooter";

const AlertDialogTitle = memo(
    React.forwardRef<React.ComponentRef<typeof AlertDialogPrimitive.Title>, React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>>(({ className, ...props }, ref) => <AlertDialogPrimitive.Title ref={ref} className={cn("dw-alert-dialog-title", className)} data-component="AlertDialogTitle" data-location="src/components/ui/shadcn/alert-dialog.tsx" {...props} />)
);
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = memo(
    React.forwardRef<React.ComponentRef<typeof AlertDialogPrimitive.Description>, React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>>(({ className, ...props }, ref) => <AlertDialogPrimitive.Description ref={ref} className={cn("dw-alert-dialog-description", className)} data-component="AlertDialogDescription" data-location="src/components/ui/shadcn/alert-dialog.tsx" {...props} />)
);
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

const AlertDialogAction = memo(
    React.forwardRef<React.ComponentRef<typeof AlertDialogPrimitive.Action>, React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>>(({ className, ...props }, ref) => <AlertDialogPrimitive.Action ref={ref} className={cn("dw-button", className)} data-variant="default" data-size="default" data-component="AlertDialogAction" data-location="src/components/ui/shadcn/alert-dialog.tsx" {...props} />)
);
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = memo(
    React.forwardRef<React.ComponentRef<typeof AlertDialogPrimitive.Cancel>, React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>>(({ className, ...props }, ref) => <AlertDialogPrimitive.Cancel ref={ref} className={cn("dw-button", "dw-alert-dialog-cancel", className)} data-variant="outline" data-size="default" data-component="AlertDialogCancel" data-location="src/components/ui/shadcn/alert-dialog.tsx" {...props} />)
);
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger };
